import {
  BIOME_IDS,
  BIOMES,
  type AdminCommand,
  type AdminRejected,
  type AdminResult,
  type EconomyReport,
  GM_SPAWN_MAX,
  GM_SPAWN_REWARDS,
  GM_SPAWNABLE_MOB_IDS,
  type GmSpawnReward,
  type InventoryState,
  isUpgradeable,
  ITEMS,
  type ItemKind,
  type ItemTemplate,
  MAX_LEVEL,
  MAX_UPGRADE_LEVEL,
  MOBS,
  RARITIES,
  type ReportRow,
  TUTORIAL_ZONE_ID,
  WORLD_EVENT_KINDS,
  WORLD_EVENT_NAMES,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { closeReport, fetchEconomy, fetchReports } from '../net/moderation.js';
import { AuthError } from '../net/session.js';
import { closeButton } from './hud.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { REPORT_REASON_LABELS } from './moderation-text.js';
import { uiText } from './text.js';
import './smith.css';
import './admin.css';
import { kitTitle } from './kit.js';

/**
 * The game master's window (ADR 0025), for an admin only: level, gold,
 * any item at any smith's level, the worn outfit upgraded at once, a heal.
 * Like every panel it only sends intents; the server checks the account's
 * flag and writes through the inventory and the ledger. At the bottom, the
 * players' reports (ADR 0037) and the gold of the game (ADR 0038), read over
 * HTTP with the character's token.
 */

export interface AdminPanelOptions {
  icons: CssAtlas | null;
  /** The character session token: the moderation routes are read with it. */
  token: string;
  onCommand: (command: AdminCommand) => void;
}

/** The ledger's reasons as the economy view names them; an unknown one shows its key. */
const GOLD_REASON_LABELS: Partial<Record<string, string>> = {
  sell: 'Revente au marchand',
  buy: 'Achat au marchand',
  admin: 'Maître du jeu',
  upgrade_gold: 'Forgeron',
  brew_gold: 'Alchimiste',
  teleport: 'Voyage',
  trade_gold: 'Échange',
  auction_pay: 'Hôtel des ventes (achat)',
  auction_sale: 'Hôtel des ventes (vente)',
  auction_fee: 'Commission de l’hôtel',
  guild_create: 'Fondation de guilde',
};

/** « 24/09 14:32 »: the day and the hour, in the admin's clock. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function gold(amount: number): string {
  return `${amount.toLocaleString('fr-FR')} or`;
}

function failureText(cause: unknown): string {
  return cause instanceof AuthError ? cause.message : 'Le serveur n’a pas répondu.';
}

const REFUSALS: Record<AdminRejected['reason'], string> = {
  invalid: 'Commande invalide.',
  'not-admin': 'Ce compte n’est plus admin.',
  'unknown-item': 'Objet inconnu.',
  'bag-full': 'Ton sac est plein.',
  'not-upgradeable': 'Cet objet ne s’améliore pas.',
  dead: 'Attends de te relever.',
  blocked: 'Aucune case praticable libre par là.',
  'event-busy': 'Un événement est déjà en cours.',
  'no-event': 'Aucun événement à arrêter ici (monde ouvert seulement).',
  'no-aerie': 'Pas dans l’Aire, ou Shefira n’est pas là.',
  'not-found': 'Personne de ce nom dans ta zone.',
};

const KIND_LABELS: Record<ItemKind | 'all', string> = {
  all: 'Tout',
  equipment: 'Équipement',
  consumable: 'Consommables',
  material: 'Matériaux',
};

const GOLD_PRESETS = [1_000, 10_000, 100_000, 1_000_000];

const SPAWN_REWARD_LABELS: Record<GmSpawnReward, string> = {
  full: 'XP + butin',
  xp: 'XP seule',
  none: 'Rien',
};

/** By level, then name: the list reads like the valley, from the meadow to the bosses. */
function spawnableByLevel(): typeof GM_SPAWNABLE_MOB_IDS {
  return [...GM_SPAWNABLE_MOB_IDS].sort(
    (a, b) =>
      (MOBS[a].level ?? 0) - (MOBS[b].level ?? 0) || MOBS[a].name.localeCompare(MOBS[b].name),
  ) as typeof GM_SPAWNABLE_MOB_IDS;
}

function spawnLabel(id: (typeof GM_SPAWNABLE_MOB_IDS)[number]): string {
  const mob = MOBS[id];
  const level = mob.level === undefined ? '' : `Nv ${String(mob.level)} · `;
  const rank = mob.rank === 'ordinary' ? '' : ' (boss)';
  return `${level}${uiText(mob.name)}${rank}`;
}

/** Equipment first, then by level and rarity: the list reads like the loot tiers. */
function byTier(a: ItemTemplate, b: ItemTemplate): number {
  const kinds: ItemKind[] = ['equipment', 'consumable', 'material'];
  return (
    kinds.indexOf(a.kind) - kinds.indexOf(b.kind) ||
    a.levelRequired - b.levelRequired ||
    RARITIES.indexOf(a.rarity) - RARITIES.indexOf(b.rarity) ||
    a.name.localeCompare(b.name)
  );
}

/** Lowercased and unaccented, so « epee » finds « Épée ». */
function folded(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function numberInput(min: number, max: number, value: number): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'gm__number';
  input.min = String(min);
  input.max = String(max);
  input.step = '1';
  input.value = String(value);
  return input;
}

/** The input's value clamped to its bounds, or undefined when it is not a whole number. */
function readNumber(input: HTMLInputElement): number | undefined {
  const value = Number(input.value);
  if (!Number.isInteger(value)) return undefined;
  return Math.min(Number(input.max), Math.max(Number(input.min), value));
}

function levelSelect(): HTMLSelectElement {
  const select = document.createElement('select');
  select.className = 'gm__select';
  for (let level = 0; level <= MAX_UPGRADE_LEVEL; level += 1) {
    const option = document.createElement('option');
    option.value = String(level);
    option.textContent = `+${String(level)}`;
    select.append(option);
  }
  return select;
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'smith__go gm__go';
  element.textContent = uiText(label);
  element.addEventListener('click', onClick);
  return element;
}

export class AdminPanel {
  private readonly root: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly chosen: HTMLElement;
  private readonly message: HTMLElement;
  private readonly search: HTMLInputElement;
  private readonly kind: HTMLSelectElement;
  private readonly quantity: HTMLInputElement;
  private readonly itemLevel: HTMLSelectElement;
  private readonly level: HTMLInputElement;
  private readonly gold: HTMLInputElement;
  private readonly wornLevel: HTMLSelectElement;
  private readonly tileX: HTMLInputElement;
  private readonly tileY: HTMLInputElement;
  private readonly status: HTMLElement;
  private readonly reports: HTMLElement;
  private readonly reportsNote: HTMLElement;
  private readonly economy: HTMLElement;
  private readonly playerList: HTMLElement;
  private selected: ItemTemplate | undefined;
  private inventory: InventoryState | undefined;
  private currentLevel = 1;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'F3') {
      event.preventDefault();
      this.toggle();
    } else if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
    }
  };

  constructor(private readonly options: AdminPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith gm';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Maître du jeu'));

    const title = document.createElement('h2');
    title.className = 'smith__title gm__title';
    kitTitle(title, 'gm');
    title.textContent = uiText('[GM] Maître du jeu');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    this.status = document.createElement('p');
    this.status.className = 'smith__muted';

    // Level and gold: one line each.
    this.level = numberInput(1, MAX_LEVEL, 1);
    const levelRow = this.row(
      'Niveau',
      this.level,
      button('Fixer', () => {
        const level = readNumber(this.level);
        if (level !== undefined) this.send({ action: 'set_level', level });
      }),
      button('Max', () => {
        this.send({ action: 'set_level', level: MAX_LEVEL });
      }),
    );
    this.gold = numberInput(1, 10_000_000, 10_000);
    const goldRow = this.row(
      'Or',
      this.gold,
      button('Donner', () => {
        const amount = readNumber(this.gold);
        if (amount !== undefined) this.send({ action: 'grant_gold', amount });
      }),
    );
    const presets = document.createElement('div');
    presets.className = 'gm__presets';
    for (const amount of GOLD_PRESETS) {
      presets.append(
        button(`+${amount.toLocaleString('fr-FR')}`, () => {
          this.send({ action: 'grant_gold', amount });
        }),
      );
    }

    // The outfit worn, all at once, and a heal.
    this.wornLevel = levelSelect();
    this.wornLevel.value = String(MAX_UPGRADE_LEVEL);
    const wornRow = this.row(
      'Porté',
      this.wornLevel,
      button('Améliorer tout', () => {
        this.send({ action: 'upgrade_worn', level: Number(this.wornLevel.value) });
      }),
      button('Soigner', () => {
        this.send({ action: 'heal' });
      }),
    );

    // Anywhere in the zone, by its tile; the world map (M) does it by a click.
    this.tileX = numberInput(0, 1023, 0);
    this.tileY = numberInput(0, 1023, 0);
    const teleportRow = this.row(
      'Aller',
      this.tileX,
      this.tileY,
      button('Téléporter', () => {
        const x = readNumber(this.tileX);
        const y = readNumber(this.tileY);
        if (x !== undefined && y !== undefined) this.send({ action: 'teleport', x, y });
      }),
    );
    // Out of sight, then beside a player by name: to watch without being watched.
    const vanishRow = this.row(
      'Discret',
      button('Invisible', () => {
        this.send({ action: 'set_vanished', vanished: true });
      }),
      button('Visible', () => {
        this.send({ action: 'set_vanished', vanished: false });
      }),
    );
    const playerName = document.createElement('input');
    playerName.type = 'text';
    playerName.className = 'gm__search';
    playerName.placeholder = uiText('Nom du joueur');
    playerName.maxLength = 32;
    const sendToPlayer = (): void => {
      const name = playerName.value.trim();
      if (name.length > 0) this.send({ action: 'teleport_to_player', name });
    };
    playerName.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') sendToPlayer();
    });
    const toPlayerRow = this.row('Joueur', playerName, button('Rejoindre', sendToPlayer));
    // Who is in the zone, each a button away (SHE-65): no name to guess.
    this.playerList = document.createElement('div');
    this.playerList.className = 'gm__list gm__players';
    const listRow = this.row(
      'Connectés',
      button('Actualiser', () => {
        this.send({ action: 'list_players' });
      }),
    );
    // The tutorial has no way in from the world (ADR 0030): a button, and one back.
    const zoneRow = this.row(
      'Zone',
      button('Tutoriel', () => {
        this.send({ action: 'travel_zone', zone: TUTORIAL_ZONE_ID });
      }),
      button('Monde', () => {
        this.send({ action: 'travel_zone', zone: 'world' });
      }),
    );
    // A world event now (ADR 0050): the scenario at the biome's village, ten seconds away.
    const eventKind = document.createElement('select');
    eventKind.className = 'gm__select';
    for (const kind of WORLD_EVENT_KINDS) {
      const option = document.createElement('option');
      option.value = kind;
      option.textContent = WORLD_EVENT_NAMES[kind];
      eventKind.append(option);
    }
    const eventBiome = document.createElement('select');
    eventBiome.className = 'gm__select';
    for (const id of BIOME_IDS) {
      const option = document.createElement('option');
      option.value = id;
      option.textContent = uiText(BIOMES[id].village);
      eventBiome.append(option);
    }
    const eventRow = this.row(
      'Événement',
      eventKind,
      eventBiome,
      button('Lancer', () => {
        const biome = BIOME_IDS.find((id) => id === eventBiome.value);
        const kind = WORLD_EVENT_KINDS.find((id) => id === eventKind.value);
        if (biome !== undefined && kind !== undefined) {
          this.send({ action: 'start_event', kind, biome });
        }
      }),
      button('Arrêter', () => {
        this.send({ action: 'stop_event' });
      }),
    );
    // Shefira's scenes (ADR 0067), on this screen only: what a turn-in of her quests shows.
    const cinematicRow = this.row(
      'Dragon',
      button('Ombre', () => {
        this.send({ action: 'play_cinematic', id: 'shefira-shadow' });
      }),
      button('Brèche', () => {
        this.send({ action: 'play_cinematic', id: 'shefira-breach' });
      }),
      button('Envol', () => {
        this.send({ action: 'play_cinematic', id: 'shefira-flight' });
      }),
    );
    // Shefira's Aire (ADR 0068): in alone from anywhere, then her phases on demand.
    const aerieRow = this.row(
      'Aire',
      button('Histoire', () => {
        this.send({ action: 'aerie_start', mode: 'story' });
      }),
      button('Raid', () => {
        this.send({ action: 'aerie_start', mode: 'raid' });
      }),
      button('Sol', () => {
        this.send({ action: 'aerie_phase', phase: 'ground' });
      }),
      button('Vol', () => {
        this.send({ action: 'aerie_phase', phase: 'flight' });
      }),
      button('Rage', () => {
        this.send({ action: 'aerie_phase', phase: 'enraged' });
      }),
    );
    // Creatures around the game master, for an event of their own: they hunt at once.
    const spawnMob = document.createElement('select');
    spawnMob.className = 'gm__select gm__select--wide';
    for (const id of spawnableByLevel()) {
      const option = document.createElement('option');
      option.value = id;
      option.textContent = spawnLabel(id);
      spawnMob.append(option);
    }
    const spawnCount = numberInput(1, GM_SPAWN_MAX, 5);
    const spawnReward = document.createElement('select');
    spawnReward.className = 'gm__select';
    for (const reward of GM_SPAWN_REWARDS) {
      const option = document.createElement('option');
      option.value = reward;
      option.textContent = uiText(SPAWN_REWARD_LABELS[reward]);
      spawnReward.append(option);
    }
    const spawnRow = this.row('Créatures', spawnMob);
    const spawnActions = this.row(
      'Nombre',
      spawnCount,
      spawnReward,
      button('Poser', () => {
        const mob = GM_SPAWNABLE_MOB_IDS.find((id) => id === spawnMob.value);
        const reward = GM_SPAWN_REWARDS.find((value) => value === spawnReward.value);
        const count = readNumber(spawnCount);
        if (mob !== undefined && reward !== undefined && count !== undefined) {
          this.send({ action: 'spawn_mobs', mob, count, reward });
        }
      }),
      button('Tout retirer', () => {
        this.send({ action: 'clear_mobs' });
      }),
    );
    const teleportHint = document.createElement('p');
    teleportHint.className = 'smith__muted gm__hint';
    teleportHint.textContent = uiText('Ou clique sur la carte du monde (M).');

    // Any item: filter, pick, choose how many and at which level.
    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.className = 'gm__search';
    this.search.placeholder = uiText('Chercher un objet…');
    this.search.addEventListener('input', () => {
      this.renderGrid();
    });
    this.kind = document.createElement('select');
    this.kind.className = 'gm__select';
    for (const [value, label] of Object.entries(KIND_LABELS)) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = uiText(label);
      this.kind.append(option);
    }
    this.kind.addEventListener('change', () => {
      this.renderGrid();
    });
    const filters = document.createElement('div');
    filters.className = 'gm__row';
    filters.append(this.search, this.kind);
    this.grid = document.createElement('div');
    this.grid.className = 'gm__grid';
    this.chosen = document.createElement('p');
    this.chosen.className = 'gm__chosen';
    this.quantity = numberInput(1, 99, 1);
    this.itemLevel = levelSelect();
    this.itemLevel.addEventListener('change', () => {
      if (this.selected !== undefined) this.select(this.selected);
    });
    const grantRow = this.row(
      'Qté',
      this.quantity,
      this.itemLevel,
      button('Donner l’objet', () => {
        this.grantSelected();
      }),
    );

    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    // The players' reports, loaded on demand: the list scrolls, not the window.
    this.reports = document.createElement('div');
    this.reports.className = 'gm__list';
    this.reportsNote = document.createElement('p');
    this.reportsNote.className = 'smith__muted gm__note';
    const reportsRow = this.row(
      'Ouverts',
      button('Charger', () => {
        void this.loadReports();
      }),
      this.reportsNote,
    );

    // The gold of the game (ADR 0038), read when asked.
    this.economy = document.createElement('div');
    this.economy.className = 'gm__economy';
    const economyRow = this.row(
      'Or',
      button('Actualiser', () => {
        void this.loadEconomy();
      }),
    );

    // Typing in a field must not walk, mount or open another window.
    this.root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' && event.key !== 'F3') event.stopPropagation();
    });

    // Everything under the title scrolls in its own box: an overflow on the
    // window itself would clip the close cross on its frame.
    const body = document.createElement('div');
    body.className = 'gm__body';
    body.append(
      this.status,
      levelRow,
      goldRow,
      presets,
      wornRow,
      teleportRow,
      teleportHint,
      vanishRow,
      toPlayerRow,
      listRow,
      this.playerList,
      zoneRow,
      this.section('Événements'),
      eventRow,
      spawnRow,
      spawnActions,
      cinematicRow,
      aerieRow,
      this.section('Objets'),
      filters,
      this.grid,
      this.chosen,
      grantRow,
      this.message,
      this.section('Signalements'),
      reportsRow,
      this.reports,
      this.section('Économie'),
      economyRow,
      this.economy,
    );
    this.root.append(title, body);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
    this.renderGrid();
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.renderStatus();
    this.send({ action: 'list_players' });
  }

  /** The zone's other players, from `admin_players`: a name and a button to join them. */
  setPlayers(players: readonly { name: string; x: number; y: number }[]): void {
    if (players.length === 0) {
      const note = document.createElement('p');
      note.className = 'smith__muted gm__note';
      note.textContent = uiText('Personne d’autre dans la zone.');
      this.playerList.replaceChildren(note);
      return;
    }
    this.playerList.replaceChildren(
      ...players.map((player) => {
        const row = document.createElement('div');
        row.className = 'gm__player';
        const name = document.createElement('span');
        name.className = 'gm__player-name';
        name.textContent = `${player.name} (${String(player.x)}, ${String(player.y)})`;
        row.append(
          name,
          button('Rejoindre', () => {
            this.send({ action: 'teleport_to_player', name: player.name });
          }),
        );
        return row;
      }),
    );
  }

  close(): void {
    this.root.hidden = true;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.renderStatus();
  }

  setLevel(level: number): void {
    this.currentLevel = level;
    this.level.value = String(level);
    this.renderStatus();
  }

  showResult(result: AdminResult): void {
    const texts: Record<AdminResult['action'], string> = {
      set_level: `Niveau ${String(this.currentLevel)}.`,
      grant_gold: 'Or ajouté.',
      grant_item: 'Objet ajouté au sac.',
      upgrade_worn: `${String(result.count)} pièce(s) portée(s) améliorée(s).`,
      heal: 'Vie et énergie pleines.',
      teleport: 'Téléporté.',
      travel_zone: 'Arrivé.',
      start_event: 'Événement lancé : annonce à tous, attaque dans 10 secondes.',
      stop_event: 'Événement arrêté.',
      play_cinematic: 'Cinématique jouée.',
      aerie_start: 'En route pour l’Aire de Shefira.',
      aerie_phase: 'Shefira passe à la phase demandée.',
      spawn_mobs: `${String(result.count)} créature(s) posée(s) autour de toi.`,
      clear_mobs: `${String(result.count)} créature(s) retirée(s).`,
      set_vanished:
        result.count === 1
          ? 'Invisible : personne ne te voit, ni les créatures. Tu restes estompé à ton écran.'
          : 'De nouveau visible.',
      teleport_to_player: 'Arrivé à côté du joueur.',
      list_players: `${String(result.count)} autre(s) joueur(s) dans la zone.`,
    };
    this.say(texts[result.action], 'success');
  }

  showRejected(rejection: AdminRejected): void {
    this.say(REFUSALS[rejection.reason], 'refused');
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private send(command: AdminCommand): void {
    this.message.textContent = '';
    this.options.onCommand(command);
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
  }

  private grantSelected(): void {
    const template = this.selected;
    if (template === undefined) {
      this.say('Choisis un objet.', 'refused');
      return;
    }
    const quantity = readNumber(this.quantity);
    if (quantity === undefined) return;
    this.send({
      action: 'grant_item',
      templateId: template.id,
      quantity,
      upgradeLevel: isUpgradeable(template) ? Number(this.itemLevel.value) : 0,
    });
  }

  private row(label: string, ...controls: HTMLElement[]): HTMLElement {
    const row = document.createElement('div');
    row.className = 'gm__row';
    const name = document.createElement('span');
    name.className = 'gm__label';
    name.textContent = uiText(label);
    row.append(name, ...controls);
    return row;
  }

  private section(label: string): HTMLElement {
    const heading = document.createElement('h3');
    heading.className = 'gm__section';
    heading.textContent = uiText(label);
    return heading;
  }

  private async loadReports(): Promise<void> {
    this.reportsNote.textContent = uiText('Chargement…');
    try {
      const { reports } = await fetchReports(this.options.token, 'open');
      this.renderReports(reports);
    } catch (cause: unknown) {
      this.reportsNote.textContent = uiText(failureText(cause));
    }
  }

  private renderReports(reports: readonly ReportRow[]): void {
    this.reportsNote.textContent = uiText(
      reports.length === 0 ? 'Aucun signalement ouvert.' : `${String(reports.length)} ouvert(s)`,
    );
    this.reports.replaceChildren();
    for (const report of reports) {
      const row = document.createElement('div');
      row.className = 'gm__report';
      const when = document.createElement('span');
      when.className = 'gm__report-date';
      when.textContent = shortDate(report.createdAt);
      const who = document.createElement('span');
      who.className = 'gm__report-who';
      who.textContent = uiText(`${report.reporterName} → ${report.targetName}`);
      who.title = who.textContent;
      const reason = document.createElement('span');
      reason.className = 'gm__report-reason';
      reason.textContent = uiText(REPORT_REASON_LABELS[report.reason]);
      const details = document.createElement('span');
      details.className = 'gm__report-details';
      details.textContent = uiText(report.details ?? '');
      details.title = details.textContent;
      const close = button('Clore', () => {
        close.disabled = true;
        closeReport(this.options.token, report.id)
          .then(() => this.loadReports())
          .catch((cause: unknown) => {
            close.disabled = false;
            this.reportsNote.textContent = uiText(failureText(cause));
          });
      });
      row.append(when, who, reason, details, close);
      this.reports.append(row);
    }
  }

  private async loadEconomy(): Promise<void> {
    this.economy.replaceChildren(this.muted('Chargement…'));
    try {
      this.renderEconomy(await fetchEconomy(this.options.token));
    } catch (cause: unknown) {
      this.economy.replaceChildren(this.muted(failureText(cause)));
    }
  }

  private renderEconomy(report: EconomyReport): void {
    const summary = document.createElement('p');
    summary.className = 'gm__economy-summary';
    summary.textContent = uiText(
      `Masse d’or : ${gold(report.supply)} · ${report.purses.toLocaleString('fr-FR')} bourse(s)`,
    );
    const columns = document.createElement('div');
    columns.className = 'gm__flows';
    columns.append(
      this.flowColumn('24 h', report.lastDay),
      this.flowColumn('7 jours', report.lastWeek),
    );
    this.economy.replaceChildren(summary, columns);
  }

  /** One period: what was created, destroyed and moved, by reason, and the net. */
  private flowColumn(label: string, flow: EconomyReport['lastDay']): HTMLElement {
    const column = document.createElement('div');
    column.className = 'gm__flow';
    const heading = document.createElement('h4');
    heading.className = 'gm__flow-title';
    heading.textContent = uiText(label);
    column.append(heading);
    const groups: [string, Record<string, number>][] = [
      ['Créé', flow.minted],
      ['Détruit', flow.burnt],
      ['Échangé', flow.transferred],
    ];
    for (const [name, byReason] of groups) {
      const entries = Object.entries(byReason).sort(([, a], [, b]) => b - a);
      const total = entries.reduce((sum, [, amount]) => sum + amount, 0);
      column.append(this.flowLine(name, gold(total), 'gm__flow-group'));
      for (const [reason, amount] of entries) {
        column.append(
          this.flowLine(GOLD_REASON_LABELS[reason] ?? reason, gold(amount), 'gm__flow-reason'),
        );
      }
    }
    const net = this.flowLine('Net', `${flow.net > 0 ? '+' : ''}${gold(flow.net)}`, 'gm__flow-net');
    net.dataset.tone = flow.net >= 0 ? 'up' : 'down';
    column.append(net);
    return column;
  }

  private flowLine(label: string, value: string, className: string): HTMLElement {
    const line = document.createElement('div');
    line.className = `gm__flow-line ${className}`;
    const name = document.createElement('span');
    name.className = 'gm__flow-label';
    name.textContent = uiText(label);
    name.title = name.textContent;
    const amount = document.createElement('span');
    amount.className = 'gm__flow-value';
    amount.textContent = uiText(value);
    line.append(name, amount);
    return line;
  }

  private muted(text: string): HTMLElement {
    const line = document.createElement('p');
    line.className = 'smith__muted';
    line.textContent = uiText(text);
    return line;
  }

  private renderStatus(): void {
    const gold = this.inventory?.gold ?? 0;
    const bag =
      this.inventory === undefined
        ? ''
        : ` · sac ${String(this.inventory.bag.length)}/${String(this.inventory.bagSize)}`;
    this.status.textContent = uiText(
      `Niveau ${String(this.currentLevel)} · ${gold.toLocaleString('fr-FR')} or${bag}`,
    );
  }

  private renderGrid(): void {
    const query = folded(this.search.value.trim());
    const kind = this.kind.value;
    const templates = [...ITEMS.values()]
      .filter((template) => kind === 'all' || template.kind === kind)
      .filter(
        (template) =>
          query === '' || folded(template.name).includes(query) || template.id.includes(query),
      )
      .sort(byTier);
    this.grid.replaceChildren();
    for (const template of templates) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'slot smith__piece';
      if (template.id === this.selected?.id) cell.classList.add('smith__piece--selected');
      cell.title = uiText(`${template.name} (niv. ${String(template.levelRequired)})`);
      const item = document.createElement('div');
      item.className = `item item--${template.rarity}`;
      paintItemIcon(item, template, this.options.icons, template.id);
      cell.append(item);
      cell.addEventListener('click', () => {
        this.select(template);
      });
      this.grid.append(cell);
    }
    if (templates.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'smith__muted';
      empty.textContent = uiText('Aucun objet.');
      this.grid.append(empty);
    }
  }

  private select(template: ItemTemplate): void {
    this.selected = template;
    const upgradeable = isUpgradeable(template);
    this.itemLevel.disabled = !upgradeable;
    if (!upgradeable) this.itemLevel.value = '0';
    this.quantity.max = String(template.kind === 'equipment' ? 30 : 99);
    this.quantity.value = String(template.kind === 'equipment' ? 1 : template.maxStack);
    this.chosen.replaceChildren();
    const icon = document.createElement('div');
    icon.className = `item item--${template.rarity}`;
    paintItemIcon(icon, template, this.options.icons, template.id);
    addLevelBadge(icon, upgradeable ? Number(this.itemLevel.value) : 0);
    const name = document.createElement('span');
    name.textContent = uiText(
      `${template.name} · niv. ${String(template.levelRequired)} · ${template.id}`,
    );
    this.chosen.append(icon, name);
    this.renderGrid();
  }
}
